import { type TContactAuthor, buildMakeUseAxios } from "@api";
import type { IAddress } from "@bbj-types/address";
import type { IContact } from "@bbj-types/contact";
import type { ContactType } from "@bbj-types/contact/enum";
import type { IEnterprise } from "@bbj-types/enterprise";
import type { Override } from "@bbj-types/utils";
import type { UserPartnerAccess } from "@root/utils/enums/structurePartner";
import { useCallback } from "react";

/**
 * Route API V2
 */
// eslint-disable-next-line
export const axiosEnterprise = buildMakeUseAxios(`${import.meta.env.VITE_ENTERPRISE_SERVICE_URL}`);

export type GetEnterprise = Override<
  IEnterprise,
  {
    author?: { lastName: string; firstName: string; createdAt?: string; updatedAt?: string };
    contacts: TContactAuthor[];
  }
>;

export const useGetEnterprise = () => {
  const [state, execute, cancel] = axiosEnterprise<GetEnterprise>(
    {
      url: "/v2/enterprises/{id}",
      method: "get",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string) => {
      return execute({
        url: `/v2/enterprises/${id}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export interface IEnterprisesFiltersBody {
  search?: string;
  activityAreas?: string[];
  placeIds?: string[];
  status?: string;
  haveHired?: boolean;
  address?: IAddress;
  limit: number;
  skip: number;
}

export interface IDashboardEnterprise extends IEnterprise {
  totalApplies: number;
  totalHired: number;
}

export type TEnterpriseForm = Pick<
  IEnterprise,
  "name" | "website" | "description" | "activityArea" | "status" | "isTemp"
>;

interface IUsePostEnterprisesDashboardBody {
  data: IDashboardEnterprise[];
  limit: number;
  skip: number;
  total: number;
  count: number;
  orderStrategy: unknown;
  metadata: unknown;
}

export const usePostEnterprisesDashboard = () => {
  const [state, execute, cancel] = axiosEnterprise<IUsePostEnterprisesDashboardBody>(
    {
      url: "/v2/enterprises/dashboard",
      method: "post",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (filters: IEnterprisesFiltersBody) => {
      return execute({
        data: filters,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export interface IEnterpriseAutocompleteParams {
  search?: string;
  limit?: number;
}

export const useGetEnterprisesAutocomplete = () => {
  const [state, execute, cancel] = axiosEnterprise<Pick<IEnterprise, "_id" | "name" | "contacts">[]>(
    {
      url: "/v2/enterprises/autocomplete",
      method: "get",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (params: IEnterpriseAutocompleteParams) => {
      return execute({
        params,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export interface IExistContact {
  contact: IContact | null;
  enterpriseId: string | null;
}

export const useGetExistsContact = () => {
  const [state, execute, cancel] = axiosEnterprise<IExistContact | undefined>(
    {
      url: "/v2/contacts/exists",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (email?: string, phone?: string, type?: ContactType) => {
      return execute({
        params: { email, phone, type },
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const usePostEnterpriseCreate = () => {
  const [state, execute, cancel] = axiosEnterprise<IEnterprise>(
    {
      url: "/v2/enterprises",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: Partial<TEnterpriseForm>) => {
      return execute({
        url: "/v2/enterprises",
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const usePatchEnterprise = () => {
  const [state, execute, cancel] = axiosEnterprise<undefined>(
    {
      url: "/v2/enterprises/{id}",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string, data: Partial<TEnterpriseForm>) => {
      return execute({
        url: `/v2/enterprises/${id}`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

type TPatchContactBody = Partial<
  Pick<IContact, "firstName" | "lastName" | "email" | "description" | "job" | "phone"> & {
    place: string;
    userAccess: UserPartnerAccess;
  }
>;

export const usePatchContact = () => {
  const [state, execute, cancel] = axiosEnterprise(
    {
      url: "/contacts/{id}",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string, data: TPatchContactBody) => {
      return execute({
        url: `/contacts/${id}`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useDeleteEnterprise = () => {
  const [state, execute, cancel] = axiosEnterprise<void>(
    {
      url: "/v2/enterprises/{id}",
      method: "DELETE",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string) => {
      return execute({
        url: `/v2/enterprises/${id}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export * from "../contact";
